import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Add20Regular,
  ArrowDownload20Regular,
  Search20Regular,
  PeopleCommunity24Regular,
  Mail24Regular,
  Call24Regular,
  Wrench24Regular,
  Edit20Regular,
  ArrowLeft24Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import { ExportarSubempreiteirosModal } from "../../components/modals/ExportarSubempreiteirosModal";
import {
  ApiError,
  getSubcontractors,
  type Subcontractor,
} from "../../subempreiteiros/subempreiteirosApi";

type EstadoSubempreiteiro = "Ativo" | "Inativo";

interface SubempreiteiroView {
  id: number;
  nome: string;
  especialidades: string[];
  contacto?: string | null;
  email?: string | null;
  telefone?: string | null;
  estado?: string | null;
  obrasAssociadas: number;
  notas?: string | null;
  logo?: string | null;
}

interface Obra {
  ref: string;
  nome: string;
  estado: string;
}

const filterOptions: Array<EstadoSubempreiteiro | "Todos"> = [
  "Todos",
  "Ativo",
  "Inativo",
];

const obrasSubempreiteiro: Obra[] = [
  {
    ref: "OBR-2024-045",
    nome: "Remodelação Apartamento T3 - Cascais",
    estado: "Em Curso",
  },
  {
    ref: "OBR-2024-032",
    nome: "Construção de Moradia Unifamiliar",
    estado: "Em Curso",
  },
  {
    ref: "OBR-2024-018",
    nome: "Ampliação de Edifício Comercial",
    estado: "Concluída",
  },
];

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para consultar sub-empreiteiros.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível carregar os sub-empreiteiros.";
};

const mapSubcontractorToView = (
  subcontractor: Subcontractor,
): SubempreiteiroView => {
  return {
    id: subcontractor.id,
    nome: subcontractor.name,
    especialidades:
      subcontractor.specialties?.map((specialty) => specialty.name) ??
      [],
    contacto: subcontractor.contactName ?? null,
    email: subcontractor.email ?? null,
    telefone: subcontractor.phoneNumber ?? null,
    estado: subcontractor.status ?? null,
    obrasAssociadas: subcontractor.projectsCount ?? 0,
    notas: subcontractor.notes ?? null,
    logo: subcontractor.logoUrl ?? null,
  };
};

const formatOptional = (value?: string | null): string =>
  value && value.trim() ? value : "—";

const formatSpecialties = (values: string[]): string => {
  if (values.length === 0) return "—";

  return values.join(", ");
};

const getEstadoBadgeClass = (estado?: string | null) => {
  switch (estado) {
    case "Ativo":
      return "bg-primary/10 text-primary";
    case "Inativo":
      return "bg-muted text-muted-foreground";
    default:
      return "bg-muted text-muted-foreground";
  }
};

const getEstadoObraClass = (estado: string) => {
  if (estado === "Em Curso")
    return "bg-chart-1/10 text-chart-1";
  if (estado === "Concluída") return "bg-primary/10 text-primary";
  return "bg-muted text-muted-foreground";
};

const getInitials = (nome: string) => {
  return nome
    .split(" ")
    .map((word) => word[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
};

export function SubempreiteirosView() {
  const navigate = useNavigate();
  const [subempreiteiros, setSubempreiteiros] = useState<
    SubempreiteiroView[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(
    null,
  );
  const [
    selectedSubempreiteiroId,
    setSelectedSubempreiteiroId,
  ] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilter, setSelectedFilter] = useState<
    EstadoSubempreiteiro | "Todos"
  >("Todos");
  const [isMobileDetailOpen, setIsMobileDetailOpen] =
    useState(false);
  const [isExportModalOpen, setIsExportModalOpen] =
    useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadSubcontractors = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const response = await getSubcontractors();
        if (!isMounted) return;

        const mapped = response.map(mapSubcontractorToView);
        setSubempreiteiros(mapped);
        setSelectedSubempreiteiroId((current) => {
          if (
            current &&
            mapped.some((item) => item.id === current)
          ) {
            return current;
          }
          return mapped[0]?.id ?? null;
        });
      } catch (error) {
        if (!isMounted) return;
        setSubempreiteiros([]);
        setSelectedSubempreiteiroId(null);
        setLoadError(parseErrorMessage(error));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadSubcontractors();

    return () => {
      isMounted = false;
    };
  }, []);

  const filteredSubempreiteiros = useMemo(
    () =>
      subempreiteiros.filter((subempreiteiro) => {
        const matchesSearch =
          subempreiteiro.nome
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          subempreiteiro.especialidades
            .join(" ")
            .toLowerCase()
            .includes(searchQuery.toLowerCase());
        const matchesFilter =
          selectedFilter === "Todos"
            ? true
            : subempreiteiro.estado === selectedFilter;

        return matchesSearch && matchesFilter;
      }),
    [subempreiteiros, searchQuery, selectedFilter],
  );

  useEffect(() => {
    if (isLoading) return;

    if (filteredSubempreiteiros.length === 0) {
      setSelectedSubempreiteiroId(null);
      return;
    }

    const hasSelection = filteredSubempreiteiros.some(
      (entry) => entry.id === selectedSubempreiteiroId,
    );
    if (!hasSelection) {
      setSelectedSubempreiteiroId(filteredSubempreiteiros[0].id);
    }
  }, [filteredSubempreiteiros, isLoading, selectedSubempreiteiroId]);

  const selectedSubempreiteiro = filteredSubempreiteiros.find(
    (s) => s.id === selectedSubempreiteiroId,
  );

  const actions = (
    <>
      <button
        className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{ borderRadius: "var(--radius-button)" }}
        onClick={() => setIsExportModalOpen(true)}
      >
        <ArrowDownload20Regular className="w-5 h-5" />
        <span className="hidden sm:inline">Exportar</span>
      </button>
      <button
        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{
          borderRadius: "var(--radius-button)",
          height: "var(--button-height)",
        }}
        onClick={() => navigate("/subempreiteiros/novo")}
      >
        <Add20Regular className="w-5 h-5" />
        Novo Sub-empreiteiro
      </button>
    </>
  );

  const handleSubempreiteiroSelect = (subempreiteiroId: number) => {
    setSelectedSubempreiteiroId(subempreiteiroId);
    setIsMobileDetailOpen(true);
  };

  const handleMobileBack = () => {
    setIsMobileDetailOpen(false);
  };

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Sub-empreiteiros"
        subtitle="Gestão de sub-empreiteiros e especialidades"
        actions={actions}
      />

      <div className="flex-1 flex overflow-hidden">
        <div
          className={`${
            isMobileDetailOpen ? "hidden md:flex" : "flex"
          } flex-col w-full md:w-80 lg:w-96 border-r border-border bg-muted/30`}
        >
          <div className="p-4 border-b border-border bg-card">
            <div className="relative">
              <Search20Regular className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-text" />
              <input
                type="text"
                placeholder="Procurar sub-empreiteiros..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                style={{ borderRadius: "var(--radius-button)" }}
              />
            </div>

            <div className="flex flex-wrap gap-2 mt-3">
              {filterOptions.map((option) => (
                <button
                  key={option}
                  onClick={() => setSelectedFilter(option)}
                  className={`px-3 py-1.5 rounded transition-colors ${
                    selectedFilter === option
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary hover:bg-accent border border-border text-foreground"
                  }`}
                  style={{
                    borderRadius: "var(--radius)",
                    fontSize: "var(--text-caption)",
                    fontWeight:
                      selectedFilter === option
                        ? "var(--font-weight-semi-bold)"
                        : "var(--font-weight-normal)",
                  }}
                >
                  {option}
                </button>
              ))}
            </div>

            <div className="text-sm text-[var(--muted-foreground)] mt-3">
              {isLoading
                ? "A carregar sub-empreiteiros..."
                : `${filteredSubempreiteiros.length} sub-empreiteiro${
                    filteredSubempreiteiros.length !== 1
                      ? "es"
                      : ""
                  } encontrado${
                    filteredSubempreiteiros.length !== 1
                      ? "s"
                      : ""
                  }`}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {isLoading && (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <PeopleCommunity24Regular className="text-[var(--muted-foreground)] mb-4" />
                <p className="text-[var(--muted-foreground)] text-center">
                  A carregar sub-empreiteiros...
                </p>
              </div>
            )}

            {!isLoading && loadError && (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <PeopleCommunity24Regular className="text-[var(--muted-foreground)] mb-4" />
                <p className="text-[var(--destructive)] text-center">
                  {loadError}
                </p>
              </div>
            )}

            {!isLoading &&
              !loadError &&
              filteredSubempreiteiros.map((subempreiteiro) => (
                <button
                  key={subempreiteiro.id}
                  onClick={() =>
                    handleSubempreiteiroSelect(subempreiteiro.id)
                  }
                  className={`w-full text-left p-4 border-b border-border transition-colors relative ${
                    selectedSubempreiteiroId ===
                    subempreiteiro.id
                      ? "bg-accent/50"
                      : ""
                  }`}
                  style={{
                    backgroundColor:
                      selectedSubempreiteiroId ===
                      subempreiteiro.id
                        ? "var(--accent)"
                        : "var(--card)",
                  }}
                  onMouseEnter={(e) => {
                    if (
                      selectedSubempreiteiroId !==
                      subempreiteiro.id
                    ) {
                      e.currentTarget.style.backgroundColor =
                        "var(--accent)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (
                      selectedSubempreiteiroId !==
                      subempreiteiro.id
                    ) {
                      e.currentTarget.style.backgroundColor =
                        "var(--card)";
                    }
                  }}
                >
                  {selectedSubempreiteiroId ===
                    subempreiteiro.id && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary" />
                  )}

                  <div className="flex items-start gap-3 pl-2">
                    {subempreiteiro.logo ? (
                      <img
                        src={subempreiteiro.logo}
                        alt={subempreiteiro.nome}
                        className="w-10 h-10 object-cover flex-shrink-0"
                        style={{
                          borderRadius: "var(--radius)",
                        }}
                      />
                    ) : (
                      <div
                        className="w-10 h-10 bg-primary/10 flex items-center justify-center flex-shrink-0"
                        style={{
                          fontSize: "var(--text-caption)",
                          fontWeight:
                            "var(--font-weight-semi-bold)",
                          color: "var(--primary)",
                          borderRadius: "var(--radius)",
                        }}
                      >
                        {getInitials(subempreiteiro.nome)}
                      </div>
                    )}

                    <div className="flex-1 min-w-0">
                      <p
                        className="truncate"
                        style={{
                          fontWeight:
                            "var(--font-weight-semi-bold)",
                        }}
                      >
                        {subempreiteiro.nome}
                      </p>

                      <p
                        className="text-secondary-text mt-0.5"
                        style={{
                          fontSize: "var(--text-caption)",
                        }}
                      >
                        {formatSpecialties(
                          subempreiteiro.especialidades,
                        )}
                      </p>

                      <p
                        className="text-secondary-text mt-1"
                        style={{
                          fontSize: "var(--text-caption)",
                        }}
                      >
                        {subempreiteiro.obrasAssociadas} obras
                        associadas
                      </p>
                    </div>
                  </div>
                </button>
              ))}

            {!isLoading &&
              !loadError &&
              filteredSubempreiteiros.length === 0 && (
                <div className="p-8 text-center text-secondary-text">
                  <p>Nenhum sub-empreiteiro encontrado</p>
                </div>
              )}
          </div>
        </div>

        <div
          className={`${
            isMobileDetailOpen ? "flex" : "hidden md:flex"
          } flex-1 flex-col overflow-y-auto bg-background`}
        >
          {!selectedSubempreiteiro ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center max-w-md">
                <PeopleCommunity24Regular className="w-16 h-16 mx-auto text-secondary-text mb-4" />
                <p
                  className="text-secondary-text"
                  style={{ fontSize: "var(--text-base)" }}
                >
                  Selecione um sub-empreiteiro para ver os
                  detalhes
                </p>
              </div>
            </div>
          ) : (
            <div className="p-4 md:p-6 space-y-6">
              <button
                onClick={handleMobileBack}
                className="md:hidden flex items-center gap-2 text-secondary-text hover:text-foreground transition-colors mb-4"
              >
                <ArrowLeft24Regular className="w-6 h-6" />
                <span>Voltar</span>
              </button>

              <div
                className="bg-card border border-border p-6"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    {selectedSubempreiteiro.logo ? (
                      <img
                        src={selectedSubempreiteiro.logo}
                        alt={selectedSubempreiteiro.nome}
                        className="w-16 h-16 object-cover flex-shrink-0"
                        style={{
                          borderRadius: "var(--radius)",
                        }}
                      />
                    ) : (
                      <div
                        className="w-16 h-16 bg-primary/10 flex items-center justify-center flex-shrink-0"
                        style={{
                          fontSize: "var(--text-large)",
                          fontWeight:
                            "var(--font-weight-semi-bold)",
                          color: "var(--primary)",
                          borderRadius: "var(--radius)",
                        }}
                      >
                        {getInitials(selectedSubempreiteiro.nome)}
                      </div>
                    )}

                    <div>
                      <h3
                        className="text-foreground"
                        style={{
                          fontSize: "var(--text-large)",
                          fontWeight:
                            "var(--font-weight-semi-bold)",
                          lineHeight: "1.3",
                        }}
                      >
                        {selectedSubempreiteiro.nome}
                      </h3>

                      <p
                        className="text-secondary-text mt-1"
                        style={{ fontSize: "var(--text-base)" }}
                      >
                      {formatSpecialties(
                        selectedSubempreiteiro.especialidades,
                      )}
                    </p>

                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <span
                          className={`inline-flex px-2 py-1 rounded ${getEstadoBadgeClass(selectedSubempreiteiro.estado)}`}
                          style={{
                            fontSize: "var(--text-caption)",
                            borderRadius: "var(--radius)",
                          }}
                        >
                          {formatOptional(selectedSubempreiteiro.estado)}
                        </span>
                        {selectedSubempreiteiro.obrasAssociadas >
                          0 && (
                          <span
                            className="inline-flex px-2 py-1 bg-accent text-accent-foreground rounded"
                            style={{
                              fontSize: "var(--text-caption)",
                              borderRadius: "var(--radius)",
                            }}
                          >
                            {
                              selectedSubempreiteiro.obrasAssociadas
                            }{" "}
                            obras ativas
                          </span>
                        )}
                      </div>

                      <div className="mt-3 space-y-1">
                        <p
                          className="text-secondary-text"
                          style={{
                            fontSize: "var(--text-caption)",
                          }}
                        >
                          Contacto Principal
                        </p>
                        <p
                          style={{
                            fontWeight:
                              "var(--font-weight-semi-bold)",
                          }}
                        >
                          {formatOptional(
                            selectedSubempreiteiro.contacto,
                          )}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() =>
                        navigate(
                          `/subempreiteiros/editar/${selectedSubempreiteiro.id}`,
                        )
                      }
                      className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
                      style={{
                        borderRadius: "var(--radius-button)",
                      }}
                    >
                      <Edit20Regular className="w-5 h-5" />
                      <span>Editar Sub-empreiteiro</span>
                    </button>
                  </div>
                </div>
              </div>

              <div
                className="bg-card border border-border p-6 space-y-4"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <h4
                  className="text-foreground"
                  style={{
                    fontSize: "var(--text-section-title)",
                    fontWeight: "var(--font-weight-semi-bold)",
                  }}
                >
                  Informação de Contacto
                </h4>

                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <Mail24Regular className="w-5 h-5 text-secondary-text flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p
                        className="text-secondary-text"
                        style={{
                          fontSize: "var(--text-caption)",
                        }}
                      >
                        Email
                      </p>
                      <p className="mt-0.5">
                        {formatOptional(
                          selectedSubempreiteiro.email,
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Call24Regular className="w-5 h-5 text-secondary-text flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p
                        className="text-secondary-text"
                        style={{
                          fontSize: "var(--text-caption)",
                        }}
                      >
                        Telefone
                      </p>
                      <p className="mt-0.5">
                        {formatOptional(
                          selectedSubempreiteiro.telefone,
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3">
                    <Wrench24Regular className="w-5 h-5 text-secondary-text flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p
                        className="text-secondary-text"
                        style={{
                          fontSize: "var(--text-caption)",
                        }}
                      >
                        Especialidade
                      </p>
                      <p className="mt-0.5">
                        {formatSpecialties(
                          selectedSubempreiteiro.especialidades,
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className="bg-card border border-border p-6 space-y-4"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <h4
                  className="text-foreground"
                  style={{
                    fontSize: "var(--text-section-title)",
                    fontWeight: "var(--font-weight-semi-bold)",
                  }}
                >
                  Obras Associadas
                </h4>

                <div className="space-y-3">
                  {obrasSubempreiteiro.map((obra, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-muted/30 rounded border border-border hover:border-primary/50 transition-colors"
                      style={{ borderRadius: "var(--radius)" }}
                    >
                      <div className="flex-1">
                        <p
                          style={{
                            fontWeight:
                              "var(--font-weight-semi-bold)",
                          }}
                        >
                          {obra.ref}
                        </p>
                        <p
                          className="text-secondary-text mt-0.5"
                          style={{
                            fontSize: "var(--text-caption)",
                          }}
                        >
                          {obra.nome}
                        </p>
                        <div className="mt-2">
                          <span
                            className={`inline-flex px-2 py-0.5 rounded ${getEstadoObraClass(obra.estado)}`}
                            style={{
                              fontSize: "var(--text-caption)",
                              borderRadius: "var(--radius)",
                            }}
                          >
                            {obra.estado}
                          </span>
                        </div>
                      </div>
                      <button
                        className="px-3 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors"
                        style={{
                          borderRadius: "var(--radius-button)",
                          fontSize: "var(--text-caption)",
                        }}
                      >
                        Ver Obra
                      </button>
                    </div>
                  ))}

                  {obrasSubempreiteiro.length === 0 && (
                    <p className="text-secondary-text text-center py-4">
                      Nenhuma obra associada
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <ExportarSubempreiteirosModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        totalSubempreiteirosFiltrados={filteredSubempreiteiros.length}
      />
    </div>
  );
}
